"use client";

import { useEffect } from "react";

import { logClient } from "@/integrations/posthog/log-client";

/**
 * Emits one `page_not_found` log record when a 404 boundary renders.
 *
 * Client-side on purpose, not in `onRequestError` or the server component:
 * the overwhelming majority of 404 traffic is bot probes that never execute
 * JavaScript, so logging from the browser filters them for free and what
 * remains is the interesting set — real people following broken links. The
 * SDK auto-stamps the session id, so a record jumps straight to the replay.
 *
 * PII: only the pathname is logged, never the query string — share links to
 * /konfigurera carry the customer's typed design text in their params. The
 * referrer is stripped to origin + pathname for the same reason.
 */
export function NotFoundLogger({ boundary }: { boundary: "root" | "locale" }) {
  useEffect(() => {
    let referrer: string | null = null;
    try {
      if (document.referrer) {
        const parsed = new URL(document.referrer);
        referrer = `${parsed.origin}${parsed.pathname}`;
      }
    } catch {
      // Unparseable referrer — drop it rather than lose the record.
    }

    logClient("info", "page_not_found", {
      path: window.location.pathname,
      boundary,
      referrer,
    });
  }, [boundary]);

  return null;
}
